import React, {Component} from 'react';
import {withStyles} from '@material-ui/core/styles/index';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import {emailRegex, getPenguinsData, snakeCase, validateRequiredFields} from '../helpers';
import asFormControl from '../components/FormControl';


const styles = theme => ({
  form: {
    width: '100%',
    margin: '0 auto',
  },
  textField: {
    backgroundColor: theme.palette.secondary.light,
  },
  input: {
    color: 'rgb(27, 101, 166)',
    fontWeight: 'bold',
  },
  muiInput: {
    [theme.breakpoints.down('sm')]: {
      padding: '0.5rem 0.5rem'
    }
  },
  button: {
    fontWeight: 'bold',
    boxShadow: '0 30px 30px -10px rgba(0,0,0,0.3)'
  },
  // bug on MUI's side where the default props are overriding the actual props
  // have to use !important as a workaround
  buttonDisabled: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    boxShadow: '0 30px 30px -10px rgba(0,0,0,0.3) !important',
    color: '#FFFFFF !important'
  },
  agreement: {
    color: '#455460',
    fontSize: '0.75rem',
    lineHeight: '1rem',
    letterSpacing: '0.2px',
    textAlign: 'left',
  },
  agreementLink: {
    color: '#455460',
    fontWeight: 'bold',
  },
  terms: {
    color: '#b0b0b0',
    margin: '1rem auto',
    textAlign: 'initial'
  }
});


const baseRequiredFields = ['signup_first_name', 'signup_last_name', 'signup_email', 'signup_phone'];
const utmFields = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_fb", "utm_goog"];


function getParameterByName(name) {
  name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
  var regex = new RegExp("[\\?&]" + name + "=([^&#]*)"),
    results = regex.exec(location.search);
  return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
}

class MembershipForm extends Component {
  constructor(props) {
    super(props);
    this.form = React.createRef();
    this.FormComponent = asFormControl(this, baseRequiredFields);
    const utmValues = {};
    forEach(utmFields, (value, i) => {
      utmValues[value] = "";
    });
    this.state = {
      errors: [],
      openErrorDialog: false,
      formData: {},
      formErrors: {},
      agreedToTerms: false,
      ...utmValues,
      url: '',
      showErrors: false,
    };
    this.sessionKey = getPenguinsData(['view_data', 'session_key']);
    if (props.triggerSubmit) props.triggerSubmit(this.handleSubmit);
  }

  componentDidMount() {
    const input = document.getElementById('penguins_data');
    const utmValues = {};
    forEach(utmFields, (value, i) => {
      utmValues[value] = getParameterByName(value);
    });
    this.setState({url: window.location.href, ...utmValues});
    if (input && process.env.REACT_STATIC_ENV === 'production') {
      const pData = JSON.parse(input.value);
      const {agreedToTerms = false, errors = [], formData = {}, formErrors = {}} = pData;
      this.setState({
        agreedToTerms,
        errors,
        formData,
        formErrors,
        openErrorDialog: errors.length > 0
      })
    }
  }

  updateFormData = name => (e, value = null) => {
    const {formData} = this.state;
    formData[snakeCase(name)] = value !== null ? value : e.target.value;
    this.setState({formData});
  };

  validateFormData = () => {
    const {formData} = this.state;
    const nameRegExp = /^[a-zA-Z][a-zA-Z '.-]{0,30}[a-zA-Z.]$/;
    const nameFields = ['signup_first_name', 'signup_last_name'];
    let formErrors = {};
    let requiredFields = baseRequiredFields;
    formErrors = validateRequiredFields(requiredFields, formData, formErrors);
    for (let field of nameFields) {
      if (requiredFields.indexOf(field) > -1 && !(nameRegExp.test(formData[field]))) {
        formErrors[field] = 'Enter a valid name';
      }
    }
    if (!emailRegex.test(formData['signup_email'])) {
      formErrors['signup_email'] = 'Enter a valid email address';
    }
    return formErrors;
  };

  handleSubmit = e => {
    if (e) e.preventDefault();
    const formErrors = this.validateFormData();
    if (!isEmpty(formErrors)) {
      if (!this.props.onSubmit) { // no need to control scroll if form is externally controlled by parent node
        window.scrollTo(0, 0);
      } else {
        this.props.onSubmit(false);
      }
      this.setState({formErrors, showErrors: true}, () => {
        this.setState({showErrors: false});
      });
    } else if (this.props.onSubmit) {
      this.props.onSubmit(true, this.form);
    } else {
      this.form.current.submit();
    }
  };

  toggleAgreedToTerms = e => {
    this.setState({agreedToTerms: e.target.checked});
  };

  handleDialogClose = e => {
    this.setState({openErrorDialog: false});
  };

  render() {
    const {classes} = this.props;
    const {agreedToTerms, errors, showErrors} = this.state;
    const FormComponent = this.FormComponent;
    const FormComponentProps = {
      className: classes.textField,
      placeholderLabel: true,
      InputProps: {
        className: classes.input,
        inputProps: {
          className: classes.muiInput
        }
      },
      showErrors
    };

    return (
      <React.Fragment>
        <form className={classes.form} ref={this.form} action="/api/db_lead/dbcom_lead_form/" method="post"
              encType="multipart/form-data" onSubmit={this.props.onSubmit || this.handleSubmit}>
          <input type="hidden" name="csrfmiddlewaretoken" value={getPenguinsData(['csrf_token'])}/>
          {utmFields.map(value => <input key={value} type='hidden' name={value} value={this.state[value]}/>)}
          <input type="hidden" name="lead_source_code" value="C_DBCOM_HOMEPAGE_NEW"/>
          <input type="hidden" name="lead_source" value="Corporate Sales"/>
          <input type="hidden" name="url" value={this.state.url}/>
          <input type="hidden" name="redirect_url" value={`/v2/membership-pricing/?sk=${this.sessionKey}`}/>
          <input type="hidden" name="session_key" value={this.sessionKey}/>
          <FormComponent
            name='signupFirstName'
            inputProps={{
              minLength: 2,
              maxLength: 50,
              autoComplete: 'given-name',
              placeholder: 'First Name',
              className: classes.muiInput
            }}
            {...FormComponentProps}
          />
          <FormComponent
            name='signupLastName'
            inputProps={{
              minLength: 2,
              maxLength: 50,
              autoComplete: 'family-name',
              placeholder: 'Last Name'
            }}
            {...FormComponentProps}
          />
          <FormComponent
            name='signupEmail'
            inputProps={{
              minLength: 8,
              maxLength: 150,
              type: 'email',
              autoComplete: 'email',
              placeholder: 'e.g. john@example.com'
            }}
            {...FormComponentProps}
          />
          <FormComponent
            name='signupPhone'
            inputProps={{
              minLength: 7,
              maxLength: 10,
              autoComplete: 'tel',
              placeholder: '(___) ___-____'
            }}
            mask={['+', '1', ' ', '(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
            {...FormComponentProps}
          />
          {
            this.props.showTerms &&
            <div className={classes.terms}>
              <label htmlFor='terms_agreement' style={{fontSize: 12.8}}>
                <input id='terms_agreement' type='checkbox' checked={agreedToTerms} onChange={this.toggleAgreedToTerms}
                       style={{marginRight: 4}} className={classes.agreement}/>
                By checking this box, you agree to receive marketing messages from DirectBuy at the email
                address you provided. You also authorize DirectBuy to call or text you at the number you
                provided for marketing purposes. These calls / texts may be made using automated dialing or
                artificial voice technology. Your consent to receive calls / texts is not a condition of
                making a purchase from us. You may withdraw this consent at any time by emailing <a
                href='mailto:membercares@directbuy.com' className={classes.agreementLink}>membercares@directbuy.com</a>,
                or calling 844-768-6945.
                <a target="_blank" rel="noopener noreferrer" href="https://www.directbuy.com/company/privacy-policy/"
                   style={{marginLeft: 4}} className={classes.agreementLink}>Privacy Policy</a>
              </label>
            </div>
          }
          {!this.props.onSubmit && !this.props.triggerSubmit &&
          <Button
            variant='contained'
            color='primary'
            size='large'
            disabled={this.props.mustAgree ? !agreedToTerms : false}
            fullWidth
            onClick={this.handleSubmit}
            classes={{root: classes.button, disabled: classes.buttonDisabled}}>
            Start My Membership &rsaquo;
          </Button>
          }
        </form>
        <Dialog
          open={this.state.openErrorDialog}
          onClose={this.handleDialogClose}
        >
          <DialogTitle>Errors</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {errors.map(value => <p>{value}</p>)}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleDialogClose} color="primary" autoFocus>
              Dismiss
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  }
}


MembershipForm.propTypes = {
  classes: PropTypes.object.isRequired,
  showTerms: PropTypes.bool,
  mustAgree: PropTypes.bool,
  onSubmit: PropTypes.func,
  triggerSubmit: PropTypes.func,
};


export default withStyles(styles)(MembershipForm);
